/**
 * Base layout component with safe-area-inset support and dark theme.
 * When `authenticated`, shows UserMenu header, BottomNav, and Cmd/Ctrl+K shortcut.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { UserMenu } from './UserMenu';
import { BottomNav } from './BottomNav';
import { OfflineIndicator } from './OfflineIndicator';

interface LayoutProps {
  children: ReactNode;
  /** When true, shows the app header with UserMenu and BottomNav */
  authenticated?: boolean;
}

export function Layout({ children, authenticated = false }: LayoutProps) {
  const navigate = useNavigate();

  // Cmd/Ctrl+K → navigate to search page
  useEffect(() => {
    if (!authenticated) return;

    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/search');
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [authenticated, navigate]);

  return (
    <div className="min-h-screen bg-background text-text-primary">
      {authenticated && (
        <>
          <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur-sm">
            <div className="mx-auto flex max-w-screen-lg items-center justify-between px-4 py-2">
              <span className="text-sm font-bold text-accent">Peekbox</span>
              <UserMenu />
            </div>
          </header>
          <OfflineIndicator />
        </>
      )}

      <main className={`mx-auto max-w-screen-lg px-4 py-4 ${authenticated ? 'pb-24' : 'py-6'}`}>
        {children}
      </main>

      {authenticated && <BottomNav />}
    </div>
  );
}
